import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useUpdateGeoTargetingModeMutation } from '@/lib/query-options';
import { toast } from '@/hooks/useToast';
import type { CampaignData, LocationTargetingMode } from '@/lib/types';

const SUPPORTED_CHANNELS = ['SEARCH', 'SHOPPING'];

const MODE_LABEL: Record<LocationTargetingMode, string> = {
  PRESENCE: 'Presence only',
  PRESENCE_OR_INTEREST: 'Presence or interest',
};

type GeoField = 'positive_geo_target_type' | 'negative_geo_target_type';

type PendingChange = {
  field: GeoField;
  current: LocationTargetingMode;
  next: LocationTargetingMode;
};

const OPTIONS: Record<
  GeoField,
  { value: LocationTargetingMode; title: string; description: string }[]
> = {
  positive_geo_target_type: [
    {
      value: 'PRESENCE',
      title: 'Presence only',
      description: 'Show ads only to users physically located in your targets.',
    },
    {
      value: 'PRESENCE_OR_INTEREST',
      title: 'Presence or interest',
      description: 'Also show ads to users elsewhere who show interest in your targets.',
    },
  ],
  negative_geo_target_type: [
    {
      value: 'PRESENCE',
      title: 'Presence only',
      description: 'Block users physically located in your exclusions.',
    },
    {
      value: 'PRESENCE_OR_INTEREST',
      title: 'Presence or interest',
      description: 'Also block users elsewhere who show interest in your exclusions.',
    },
  ],
};

function GeoRadioGroup({
  legend,
  field,
  value,
  disabled,
  onRequestChange,
}: {
  legend: string;
  field: GeoField;
  value: LocationTargetingMode;
  disabled: boolean;
  onRequestChange: (field: GeoField, next: LocationTargetingMode) => void;
}) {
  return (
    <fieldset disabled={disabled}>
      <legend className='text-xs font-medium uppercase tracking-wider text-gray-500 mb-2'>{legend}</legend>
      <RadioGroup
        value={value}
        onValueChange={(next) => onRequestChange(field, next as LocationTargetingMode)}
        className='space-y-2'
      >
        {OPTIONS[field].map((option) => (
          <div key={option.value} className='flex items-start gap-3'>
            <RadioGroupItem value={option.value} id={`${field}-${option.value}`} className='mt-1' />
            <Label htmlFor={`${field}-${option.value}`} className='cursor-pointer font-normal'>
              <span className='block text-sm font-medium text-gray-900'>{option.title}</span>
              <span className='block text-sm text-gray-700'>{option.description}</span>
            </Label>
          </div>
        ))}
      </RadioGroup>
    </fieldset>
  );
}

export const LocationTargetingModeSection: React.FC<{ campaign: CampaignData }> = ({ campaign }) => {
  const mutation = useUpdateGeoTargetingModeMutation(campaign.slug);
  const [pending, setPending] = useState<PendingChange | null>(null);

  const positive: LocationTargetingMode = campaign.positive_geo_target_type ?? 'PRESENCE';
  const negative: LocationTargetingMode = campaign.negative_geo_target_type ?? 'PRESENCE';

  const channelType = campaign.googleads_campaign?.advertising_channel_type;
  const isUnsupportedChannel = channelType != null && !SUPPORTED_CHANNELS.includes(channelType);

  const requestChange = (field: GeoField, next: LocationTargetingMode) => {
    const current = field === 'positive_geo_target_type' ? positive : negative;
    if (next === current) return;
    setPending({ field, current, next });
  };

  const confirmChange = () => {
    if (!pending) return;
    mutation.mutate(
      { campaignId: campaign.id, body: { [pending.field]: pending.next } },
      {
        onSuccess: () => {
          setPending(null);
        },
        onError: (error) => {
          toast({
            title: 'Failed to update location targeting',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          });
          setPending(null);
        },
      }
    );
  };

  const isWidening = pending?.next === 'PRESENCE_OR_INTEREST';

  return (
    <div>
      <h3 className='text-lg font-semibold text-gray-900'>Location Targeting Precision</h3>
      <p className='text-sm text-gray-700 mt-1'>
        Control whether ads reach users physically in your locations, or also those showing interest in them.
      </p>

      {isUnsupportedChannel ? (
        <div className='mt-4 flex items-start gap-2 rounded-md border-l-4 border-[#FDE68A] bg-[#FEF3C7] p-3 text-sm text-[#A16207]'>
          <Info className='h-4 w-4 mt-0.5 shrink-0' />
          <span>Location targeting mode is not configurable on Performance Max, Smart, or Demand Gen campaigns.</span>
        </div>
      ) : (
        <div className='mt-4'>
          <GeoRadioGroup
            legend='Targeting'
            field='positive_geo_target_type'
            value={positive}
            disabled={mutation.isPending}
            onRequestChange={requestChange}
          />
        </div>
      )}

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent className='sm:max-w-[560px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Change location targeting?</DialogTitle>
          </DialogHeader>
          {pending && (
            <div className='space-y-4 text-sm text-gray-700'>
              {isWidening ? (
                <p>
                  Switching {pending.field === 'positive_geo_target_type' ? 'targeting' : 'exclusions'} to
                  &ldquo;Presence or interest&rdquo; will also reach users outside your selected locations who show
                  interest in them.
                </p>
              ) : (
                <p>
                  Switching {pending.field === 'positive_geo_target_type' ? 'targeting' : 'exclusions'} to
                  &ldquo;Presence only&rdquo; will limit reach to users physically located in your selected locations.
                </p>
              )}

              {isWidening && (
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <div className='text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1'>Best for</div>
                    <ul className='list-disc pl-4 space-y-0.5'>
                      <li>Travel, tourism</li>
                      <li>E-commerce</li>
                      <li>Brand campaigns</li>
                    </ul>
                  </div>
                  <div>
                    <div className='text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1'>
                      Not recommended for
                    </div>
                    <ul className='list-disc pl-4 space-y-0.5'>
                      <li>Local services (plumbing, restaurants, in-store retail)</li>
                      <li>Geo-restricted offers</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className='rounded-md bg-gray-50 p-3 text-gray-900'>
                <div>
                  Current: <span className='font-medium'>{MODE_LABEL[pending.current]}</span>
                </div>
                <div>
                  New: <span className='font-medium'>{MODE_LABEL[pending.next]}</span>
                </div>
              </div>

              <p className='text-gray-500'>This change can shift who sees your ads and how your budget is spent.</p>
            </div>
          )}
          <div className='flex justify-end gap-3'>
            <Button variant='outline' onClick={() => setPending(null)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={confirmChange}
              disabled={mutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5] text-white'
            >
              {mutation.isPending ? 'Changing...' : 'Change Targeting'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
